// Trade Safety Guidance Component
// Provides safety recommendations for international trade

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Shield, DollarSign, Package, FileText, Ship, 
  CheckCircle, AlertTriangle, Info, ChevronRight,
  CreditCard, ClipboardCheck, Search, Globe
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function TradeSafetyGuide() {
  const [guidance, setGuidance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('payment');

  useEffect(() => {
    fetchGuidance();
  }, []);

  const fetchGuidance = async () => {
    try {
      const res = await axios.get(`${API}/api/trade-safety/all`);
      setGuidance(res.data);
    } catch (error) {
      console.error('Failed to fetch guidance:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'low': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'high': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="trade-safety-guide">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-blue-500/20 rounded-lg">
          <Shield className="h-6 w-6 text-blue-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Trade Safety Guide</h1>
          <p className="text-sm text-slate-400">Best practices for secure international trade</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 bg-slate-800 p-1">
          <TabsTrigger value="payment" className="data-[state=active]:bg-blue-600 text-xs sm:text-sm">
            <DollarSign className="h-4 w-4 mr-1 hidden sm:block" />
            Payment
          </TabsTrigger>
          <TabsTrigger value="sample" className="data-[state=active]:bg-blue-600 text-xs sm:text-sm">
            <Package className="h-4 w-4 mr-1 hidden sm:block" />
            Samples
          </TabsTrigger>
          <TabsTrigger value="inspection" className="data-[state=active]:bg-blue-600 text-xs sm:text-sm">
            <Search className="h-4 w-4 mr-1 hidden sm:block" />
            Inspection
          </TabsTrigger>
          <TabsTrigger value="docs" className="data-[state=active]:bg-blue-600 text-xs sm:text-sm">
            <FileText className="h-4 w-4 mr-1 hidden sm:block" />
            Documents
          </TabsTrigger>
        </TabsList>

        {/* Payment Terms Tab */}
        <TabsContent value="payment" className="mt-4 space-y-4">
          <Alert className="bg-blue-500/10 border-blue-500/30">
            <Info className="h-4 w-4 text-blue-400" />
            <AlertDescription className="text-blue-300">
              For first-time buyers, always use Letter of Credit or Advance Payment for maximum protection.
            </AlertDescription>
          </Alert>

          {guidance?.payment_terms && Object.entries(guidance.payment_terms).map(([key, term]) => (
            <Card key={key} className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-blue-400" />
                    {term.name}
                  </CardTitle>
                  <Badge className={getRiskColor(term.risk_level)}>
                    {term.risk_level} risk
                  </Badge>
                </div>
                <CardDescription className="text-slate-400">
                  {term.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-slate-300 mb-2">When to use:</p>
                  <ul className="space-y-1">
                    {term.when_to_use.map((use, idx) => (
                      <li key={idx} className="text-sm text-slate-400 flex items-start gap-2">
                        <ChevronRight className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        {use}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div>
                    <p className="text-xs font-medium text-green-400 mb-1">Pros:</p>
                    <ul className="text-xs text-slate-400 space-y-1">
                      {term.pros.map((pro, idx) => (
                        <li key={idx} className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3 text-green-400" />
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-red-400 mb-1">Cons:</p>
                    <ul className="text-xs text-slate-400 space-y-1">
                      {term.cons.map((con, idx) => (
                        <li key={idx} className="flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3 text-red-400" />
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Sample Guidance Tab */}
        <TabsContent value="sample" className="mt-4">
          {guidance?.sample_guidance && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Package className="h-5 w-5 text-orange-400" />
                  {guidance.sample_guidance.title}
                </CardTitle>
                <CardDescription className="text-slate-400">
                  {guidance.sample_guidance.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-red-500/10 border-red-500/30">
                  <AlertTriangle className="h-4 w-4 text-red-400" />
                  <AlertDescription className="text-red-300 text-sm">
                    {guidance.sample_guidance.warning}
                  </AlertDescription>
                </Alert>

                <div className="space-y-3">
                  {guidance.sample_guidance.steps.map((step) => (
                    <div key={step.step} className="flex gap-4 p-3 bg-slate-900/50 rounded-lg">
                      <div className="w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-orange-400 font-bold text-sm">{step.step}</span>
                      </div>
                      <div>
                        <p className="text-white font-medium">{step.action}</p>
                        <p className="text-sm text-slate-400">{step.details}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Inspection Tab */}
        <TabsContent value="inspection" className="mt-4 space-y-4">
          {guidance?.inspection && (
            <>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Search className="h-5 w-5 text-purple-400" />
                    {guidance.inspection.title}
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    {guidance.inspection.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {guidance.inspection.types.map((type, idx) => (
                    <div key={idx} className="p-3 bg-slate-900/50 rounded-lg">
                      <p className="text-white font-medium">{type.type}</p>
                      <p className="text-xs text-purple-400 mb-2">When: {type.when}</p>
                      <div className="flex flex-wrap gap-1">
                        {type.checks.map((check, cidx) => (
                          <Badge key={cidx} variant="outline" className="text-xs border-slate-600 text-slate-300">
                            {check}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-sm text-blue-300 font-medium mb-2">Recommended Providers:</p>
                    <div className="flex flex-wrap gap-2">
                      {guidance.inspection.providers.map((provider, idx) => (
                        <Badge key={idx} className="bg-blue-500/20 text-blue-300">
                          {provider}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      Cost estimate: {guidance.inspection.cost_estimate}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Documentation Tab */}
        <TabsContent value="docs" className="mt-4 space-y-4">
          {guidance?.documentation && (
            <>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ClipboardCheck className="h-5 w-5 text-green-400" />
                    {guidance.documentation.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {guidance.documentation.categories.map((category, idx) => (
                    <div key={idx}>
                      <p className="text-white font-medium mb-2">{category.category}</p>
                      <div className="space-y-2">
                        {category.documents.map((doc, didx) => (
                          <div key={didx} className="flex items-start gap-3 p-2 bg-slate-900/50 rounded">
                            {doc.required ? (
                              <CheckCircle className="h-4 w-4 text-green-400 mt-0.5" />
                            ) : (
                              <div className="h-4 w-4 rounded-full border border-slate-600 mt-0.5" />
                            )}
                            <div>
                              <p className="text-sm text-white">
                                {doc.name}
                                {doc.required && <span className="text-red-400 ml-1">*</span>}
                              </p>
                              <p className="text-xs text-slate-400">{doc.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Incoterms Quick Reference */}
              {guidance?.incoterms && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Globe className="h-5 w-5 text-cyan-400" />
                      {guidance.incoterms.title}
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      {guidance.incoterms.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {guidance.incoterms.terms.map((term, idx) => (
                        <div key={idx} className="grid grid-cols-4 gap-2 p-2 bg-slate-900/50 rounded text-sm">
                          <div>
                            <span className="text-cyan-400 font-mono font-bold">{term.code}</span>
                          </div>
                          <div className="col-span-2">
                            <p className="text-white">{term.name}</p>
                            <p className="text-xs text-slate-400">{term.description}</p>
                          </div>
                          <div className="text-xs">
                            <p className="text-green-400">Seller: {term.seller_risk}</p>
                            <p className="text-orange-400">Buyer: {term.buyer_risk}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
